import PropTypes from 'prop-types';
import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useUser } from '@app/contexts/userContext';
import { SOCKET_EVENTS } from '@app/constants/chatConstants';
import { chatSocketService } from '@app/services/chat-socket.service';

import { useWebSocketHandlers } from '@app/hooks/chat/useWebSocketHandlers';
import { useOneToOneChat } from '@app/hooks/chat/useOneToOneChat';
import { useGroupChats } from '@app/hooks/chat/useGroupChats';
import { ChatStoreContext, useChatStore } from '@app/contexts/chatStoreContext';

const ChatContext = createContext(null);

const CHAT_STATE_KEY = 'scivenia_chat_state';

const getInitialChatState = () => ({
  conversations: [],
  activeConversation: null,
  activeChatUser: null,
  conversationMessages: [],
  conversationUnreadCounts: {},
  groupChats: [],
  activeGroupChat: null,
  groupChatMessages: [],
  groupChatMembers: [],
  groupChatUnreadCounts: {},
  globalUnreadCount: 0,
  onlineUsers: new Set(),
  typingUsers: {},
  isLoading: false,
  error: null,
  connectionStatus: 'disconnected'
});

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

const ChatProviderContent = ({ children }) => {
  const { user: authUser } = useUser();
  const {
    chatState,
    clearPersistedState,
    clearOptimisticUpdates,
  } = useChatStore();
  const initializingRef = useRef(false);
  const webSocketHandlers = useWebSocketHandlers();

  const oneToOneChat = useOneToOneChat();

  const groupChats = useGroupChats();

  useEffect(() => {
    if (authUser && !initializingRef.current) {
      initializingRef.current = true;
      chatSocketService
        .connect()
        .catch(() => {
          // Connection handled silently
        })
        .finally(() => {
          initializingRef.current = false;
        });
    } else if (!authUser) {
      clearPersistedState();
    }
  }, [authUser, clearPersistedState]);

  useEffect(() => {
    if (!authUser) return undefined;

    let isCancelled = false;
    const subscriptions = [];

    const subscribe = async () => {
      const socket = await chatSocketService.connect();
      if (!socket || isCancelled) return;

      const eventHandlers = [
        [SOCKET_EVENTS.MESSAGE_NEW, webSocketHandlers.handleNewMessage],
        [SOCKET_EVENTS.GROUP_MESSAGE_NEW, webSocketHandlers.handleNewGroupMessage],
        [SOCKET_EVENTS.NOTIFICATION_NEW, webSocketHandlers.handleNotificationNew],
        [SOCKET_EVENTS.TYPING_UPDATE, webSocketHandlers.handleTypingUpdate],
        [SOCKET_EVENTS.GROUP_TYPING_UPDATE, webSocketHandlers.handleGroupTypingUpdate],
        [SOCKET_EVENTS.PRESENCE_UPDATE, webSocketHandlers.handlePresenceUpdate],
        [SOCKET_EVENTS.GROUP_PRESENCE_UPDATE, webSocketHandlers.handleGroupPresenceUpdate],
        [SOCKET_EVENTS.GROUP_PARTICIPANT_UPDATE, webSocketHandlers.handleGroupParticipantUpdate],
      ];

      eventHandlers.forEach(([eventName, handler]) => {
        const subscribed = chatSocketService.subscribe(eventName, handler);
        if (subscribed) {
          subscriptions.push(eventName);
        }
      });
    };

    subscribe();

    return () => {
      isCancelled = true;
      subscriptions.forEach((eventName) => {
        chatSocketService.unsubscribe(eventName);
      });
    };
  }, [
    authUser,
    webSocketHandlers.handleGroupParticipantUpdate,
    webSocketHandlers.handleGroupPresenceUpdate,
    webSocketHandlers.handleGroupTypingUpdate,
    webSocketHandlers.handleNewGroupMessage,
    webSocketHandlers.handleNewMessage,
    webSocketHandlers.handleNotificationNew,
    webSocketHandlers.handlePresenceUpdate,
    webSocketHandlers.handleTypingUpdate,
  ]);

  useEffect(() => {
    if (!authUser) {
      chatSocketService.disconnect();
      clearOptimisticUpdates();
    }
  }, [authUser, clearOptimisticUpdates]);

  useEffect(
    () => () => {
      chatSocketService.disconnect();
      clearOptimisticUpdates();
    },
    [clearOptimisticUpdates]
  );

  const connectionStatus = useMemo(() => chatSocketService.getConnectionState(), []);

  const contextValue = useMemo(
    () => ({
      chatState,
      isLoading: chatState.isLoading,
      error: chatState.error,

      loadConversations: oneToOneChat.loadConversations,
      joinConversation: oneToOneChat.joinConversation,
      leaveConversation: oneToOneChat.leaveConversation,
      sendMessage: oneToOneChat.sendMessage,
      markConversationAsRead: oneToOneChat.markConversationAsRead,
      contactScientist: oneToOneChat.contactScientist,

      loadGroupChats: groupChats.loadGroupChats,
      loadGroupChatMessages: groupChats.loadGroupChatMessages,
      loadMoreGroupMessages: groupChats.loadMoreGroupMessages,
      joinGroupChat: groupChats.joinGroupChat,
      leaveGroupChat: groupChats.leaveGroupChat,
      sendGroupMessage: groupChats.sendGroupMessage,
      markGroupChatAsRead: groupChats.markGroupChatAsRead,

      startTyping: webSocketHandlers.startTyping,
      stopTyping: webSocketHandlers.stopTyping,

      connectionStatus,
      reconnect: () => chatSocketService.reconnect(),
      cleanup: () => chatSocketService.cleanup(),
      clearPersistedState,
    }),
    [
      chatState,
      oneToOneChat.loadConversations,
      oneToOneChat.joinConversation,
      oneToOneChat.leaveConversation,
      oneToOneChat.sendMessage,
      oneToOneChat.markConversationAsRead,
      oneToOneChat.contactScientist,
      groupChats.loadGroupChats,
      groupChats.loadGroupChatMessages,
      groupChats.loadMoreGroupMessages,
      groupChats.joinGroupChat,
      groupChats.leaveGroupChat,
      groupChats.sendGroupMessage,
      groupChats.markGroupChatAsRead,
      webSocketHandlers.startTyping,
      webSocketHandlers.stopTyping,
      connectionStatus,
      clearPersistedState,
    ]
  );

  return <ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>;
};

ChatProviderContent.propTypes = {
  children: PropTypes.node.isRequired,
};

export const ChatProvider = ({ children }) => {
  const [chatState, setChatState] = useState(getInitialChatState);
  const chatStateRef = useRef(chatState);
  const optimisticUpdatesRef = useRef(new Set());

  useEffect(() => {
    chatStateRef.current = chatState;
  }, [chatState]);

  const updateState = useCallback((updates) => {
    if (typeof updates === 'function') {
      setChatState((prevState) => updates(prevState));
      return;
    }

    setChatState((prev) => ({ ...prev, ...updates }));
  }, []);

  useEffect(() => {
    const conversationCount = Object.values(chatState.conversationUnreadCounts).reduce(
      (sum, count) => sum + count,
      0
    );
    const groupChatCount = Object.values(chatState.groupChatUnreadCounts).reduce(
      (sum, count) => sum + count,
      0
    );
    const newGlobalCount = conversationCount + groupChatCount;

    if (newGlobalCount !== chatState.globalUnreadCount) {
      updateState({ globalUnreadCount: newGlobalCount });
    }
  }, [
    chatState.conversationUnreadCounts,
    chatState.groupChatUnreadCounts,
    chatState.globalUnreadCount,
    updateState,
  ]);

  const addOptimisticUpdate = useCallback((id) => {
    optimisticUpdatesRef.current.add(id);
  }, []);

  const removeOptimisticUpdate = useCallback((id) => {
    optimisticUpdatesRef.current.delete(id);
  }, []);

  const clearOptimisticUpdates = useCallback(() => {
    optimisticUpdatesRef.current.clear();
  }, []);

  const clearPersistedState = useCallback(() => {
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(CHAT_STATE_KEY);
    }
  }, []);

  const showNotification = useCallback((title, body, icon) => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return null;
    }

    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }

    if (Notification.permission === 'granted') {
      const messagePreview = body?.length > 50 ? `${body.substring(0, 50)}...` : body;

      const notification = new Notification(title, {
        body: messagePreview,
        icon: icon || '/favicon.ico',
        tag: 'group-message',
        requireInteraction: false,
        silent: false
      });

      return notification;
    }

    return null;
  }, []);

  const chatStoreValue = useMemo(
    () => ({
      chatState,
      chatStateRef,
      updateState,
      addOptimisticUpdate,
      removeOptimisticUpdate,
      clearOptimisticUpdates,
      clearPersistedState,
      showNotification,
    }),
    [
      chatState,
      updateState,
      addOptimisticUpdate,
      removeOptimisticUpdate,
      clearOptimisticUpdates,
      clearPersistedState,
      showNotification,
    ]
  );

  return (
    <ChatStoreContext.Provider value={chatStoreValue}>
      <ChatProviderContent>{children}</ChatProviderContent>
    </ChatStoreContext.Provider>
  );
};

ChatProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const withChat = (Component) => {
  const WrappedComponent = (props) => (
    <ChatProvider>
      <Component {...props} />
    </ChatProvider>
  );

  WrappedComponent.displayName = `withChat(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

export const useChatStatus = () => {
  const { connectionStatus, isLoading, error, chatState } = useChat();

  return {
    isConnected: connectionStatus === 'connected',
    isConnecting: connectionStatus === 'connecting',
    isDisconnected: connectionStatus === 'disconnected',
    isLoading,
    hasError: !!error,
    error,
    globalUnreadCount: chatState.globalUnreadCount,
  };
};

export const useConversationApi = () => {
  const {
    loadConversations,
    joinConversation,
    leaveConversation,
    sendMessage,
    markConversationAsRead,
    contactScientist,
  } = useChat();

  return {
    loadConversations,
    joinConversation,
    leaveConversation,
    sendMessage,
    markConversationAsRead,
    contactScientist,
  };
};

export const useGroupChatApi = () => {
  const {
    loadGroupChats,
    loadGroupChatMessages,
    loadMoreGroupMessages,
    joinGroupChat,
    leaveGroupChat,
    sendGroupMessage,
    markGroupChatAsRead,
  } = useChat();

  return {
    loadGroupChats,
    loadGroupChatMessages,
    loadMoreGroupMessages,
    joinGroupChat,
    leaveGroupChat,
    sendGroupMessage,
    markGroupChatAsRead,
  };
};

export const useTypingApi = () => {
  const { startTyping, stopTyping } = useChat();

  return {
    startTyping,
    stopTyping,
  };
};

export const useChatConnectionApi = () => {
  const { connectionStatus, reconnect, cleanup } = useChat();

  return {
    connectionStatus,
    reconnect,
    cleanup,
  };
};

export const useEventChat = (eventId) => {
  const {
    chatState,
    joinGroupChat,
    leaveGroupChat,
    sendGroupMessage,
    markGroupChatAsRead,
  } = useChat();

  const currentEventChat = chatState.groupChats?.find((chat) => chat.event_id === eventId);
  const isInEventChat = chatState.activeGroupChat?.event_id === eventId;

  return {
    currentEventChat,
    isInEventChat,
    groupChatMessages: chatState.groupChatMessages,
    groupChatMembers: chatState.groupChatMembers,
    joinEventChat: () => currentEventChat && joinGroupChat(currentEventChat),
    leaveGroupChat,
    sendGroupMessage,
    markAsRead: () => markGroupChatAsRead(currentEventChat?.id),
  };
};

export const useConversation = (conversationId) => {
  const {
    chatState,
    joinConversation,
    leaveConversation,
    sendMessage,
    markConversationAsRead,
  } = useChat();

  const currentConversation = chatState.conversations?.find((conv) => conv.id === conversationId);
  const isInConversation = chatState.activeConversation?.id === conversationId;

  return {
    currentConversation,
    isInConversation,
    conversationMessages: chatState.conversationMessages,
    joinConversation: () => currentConversation && joinConversation(currentConversation),
    leaveConversation,
    sendMessage,
    markAsRead: () => markConversationAsRead(conversationId),
  };
};
